// Pre-translation service.
//
// Solves foreign-book loading + audio stalls by having the WHOLE book's
// English ready before it's read, instead of translating one page at a time
// while the reader (and the read-aloud loop) wait.
//
// A pre-translation is a paragraph-level map: hash(sourceParagraph) -> English.
// It's pagination-proof — paragraphs are atomic, so any page's paragraphs map
// straight to English no matter how the page happens to break. Two sources:
//   • Classics: a build-time artifact at classics/translations/<id>.en.json
//     (scripts/pretranslate_classics.py), fetched on open.
//   • Imported books: generated on import into Dexie (see buildImportedPretranslation).
//
// At read time TranslatePanel asks pageEnglish() first; a full hit skips Gemma
// entirely and writes the normal page-cache the display + TTS already read from.

import { db } from "@/services/db";
import { classicsUrl } from "@/services/classicsService";
import { textHash, normalizeForHash } from "@/components/TranslatePanel";
import { remoteUrl } from "@/services/remoteConfig";
import { sanitizeTranslation } from "@/services/sanitizeTranslation";

export interface PretranslationPair { s: string; e: string }

export interface ParaMap {
  byHash: Map<string, string>;         // exact: hash(strippedSource) -> english
  entries: { s: string; e: string }[]; // strippedSource + english, for substring match
}
const memMaps = new Map<string, ParaMap>();

// Keying: strip ALL whitespace before hashing so a match survives spacing
// differences between the reader's DOM textContent and the batch's XHTML
// extraction (inline markup, CJK furigana). Only the KEY is stripped; the
// stored English is untouched.

/** Every artifact reaches the reader through buildMap, so sanitising here
 *  also cleans the contaminated copy ALREADY CACHED in Dexie on his device.
 *  Repairing only the JSON on disk would not have reached him without a
 *  re-download. See sanitizeTranslation.ts for what is wrong and why. */
function buildMap(pairs: PretranslationPair[]): ParaMap {
  const byHash = new Map<string, string>();
  const entries: { s: string; e: string }[] = [];
  for (const p of pairs) {
    if (!p.s || !p.e) continue;
    const clean = sanitizeTranslation(p.s, p.e);
    if (!clean) continue;
    const stripped = normalizeForHash(p.s).replace(/\s+/g, "");
    byHash.set(textHash(stripped), clean);
    entries.push({ s: stripped, e: clean });
  }
  return { byHash, entries };
}

// The seeded starter shelf ships the foreign originals under friendly names
// (die-verwandlung-de.epub …) that are byte-for-byte the same works as the
// numbered classics-store copies (verified 117/117 identical paragraphs), so
// they reuse the SAME shipped translation artifact. Alias them to the id.
const STARTER_ALIAS: Record<string, string> = {
  "die-verwandlung-de.epub": "22367",
  "also-sprach-zarathustra-de.epub": "7205",
  "candide-fr.epub": "4650",
  "don-quijote-es.epub": "2000",
  "pinocchio-it.epub": "52484",
  "botchan-ja.epub": "900752",
};

/** Translation-artifact id for a classic/starter book from its filename. */
export function classicIdFromFileName(fileName?: string): string | null {
  if (!fileName) return null;
  const alias = STARTER_ALIAS[fileName.toLowerCase()];
  if (alias) return alias;
  const m = fileName.match(/^(\d+)\.epub$/i);
  return m ? m[1] : null;
}

// ── Title fallback ───────────────────────────────────────────────────────
// Binding by content sha256 is exact but BRITTLE: it only works when the
// user's epub is byte-identical to the copy the artifact was generated from.
// A different Kindle/Tolino export, a re-download, or a re-zip of the same
// book yields a different sha — and the reader then silently fell back to
// live per-page translation (~50s/page) forever, even with a complete
// artifact sitting on disk. QualityLand 2.0 hit exactly this. So: also allow
// binding by normalized title, verified against real paragraphs below.
const TITLE_ALIAS: Record<string, string> = {
  "qualityland 2.0":
    "8c0de1f9752811caf79039db27cfb66e61cb82c3fb132cf6af8e241dae96bcfc",
  "die verwandlung": "22367",
  "also sprach zarathustra": "7205",
  "candide": "4650",
  "botchan": "900752",
  "坊っちゃん": "900752",
  "don quijote": "2000",
  "pinocchio": "52484",
};

/** Lowercased, punctuation-flattened title key. Dots survive so "2.0" keys
 *  cleanly; NFC first so umlauts from different toolchains compare equal. */
function normalizeTitle(s?: string): string {
  if (!s) return "";
  return s
    .normalize("NFC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}. ]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Artifact id for a book title, or null. Real-world titles carry subtitles
 *  and edition noise ("QualityLand 2.0: Kein Fehler im System"), so after the
 *  exact hit we accept an alias key that appears inside the title. */
function titleAliasId(title?: string, author?: string): string | null {
  const t = normalizeTitle(title);
  if (!t) return null;
  if (TITLE_ALIAS[t]) return TITLE_ALIAS[t];
  const a = normalizeTitle(author);
  if (a && TITLE_ALIAS[`${t} ${a}`]) return TITLE_ALIAS[`${t} ${a}`];
  for (const key of Object.keys(TITLE_ALIAS)) {
    if (key.length >= 6 && t.includes(key)) return TITLE_ALIAS[key];
  }
  return null;
}

// ── Artifact fetching ────────────────────────────────────────────────────
// Cached by ARTIFACT ID (not bookId) so a candidate can be fetched, verified
// against the book's paragraphs, and rejected without ever being committed to
// the book — binding the wrong book's map is worse than not binding at all.
const artifactCache = new Map<string, ParaMap | null>();

async function fetchArtifactMap(classicId: string): Promise<ParaMap | null> {
  if (artifactCache.has(classicId)) return artifactCache.get(classicId) ?? null;
  let map: ParaMap | null = null;
  try {
    const res = await fetch(classicsUrl(`translations/${classicId}.en.json`));
    if (res.ok) {
      const data = (await res.json()) as { pairs?: PretranslationPair[] };
      if (data.pairs?.length) map = buildMap(data.pairs);
    }
  } catch {
    map = null;
  }
  artifactCache.set(classicId, map);
  return map;
}

/** Load a classic's pre-translation artifact (fetched once, cached in memory). */
export async function loadClassicPretranslation(
  bookId: string,
  classicId: string,
): Promise<boolean> {
  if (memMaps.has(bookId)) return (memMaps.get(bookId)?.byHash.size ?? 0) > 0;
  const map = await fetchArtifactMap(classicId);
  if (!map) return false;
  memMaps.set(bookId, map);
  return true;
}

/** Load an imported book's pre-translation from Dexie (built on import). */
export async function loadImportedPretranslation(bookId: string): Promise<boolean> {
  if (memMaps.has(bookId)) return (memMaps.get(bookId)?.byHash.size ?? 0) > 0;
  try {
    const row = await db.pretranslations.get(bookId);
    if (!row?.pairs?.length) return false;
    memMaps.set(bookId, buildMap(row.pairs));
    return true;
  } catch {
    return false;
  }
}

const LOG = "[pv-pretranslate]";

/** How many of the book's real paragraphs we test a candidate artifact with,
 *  and the fraction that must resolve for the binding to be trusted. Kept low
 *  (30%) on purpose: a sample inevitably lands on front matter, chapter
 *  headings and page-edge fragments that legitimately miss, while a WRONG
 *  book's artifact scores ~0. */
const VERIFY_SAMPLE = 20;
const VERIFY_MIN_RATE = 0.3;

/** How many of `paras` a candidate map can resolve. Exposed so callers (and
 *  future debug UI) can score a binding without committing to it. Sampling is
 *  spread evenly across the book rather than taken from the front, where title
 *  pages and copyright blocks are never in the artifact. */
export function sampleMatchRate(
  map: ParaMap,
  paras: string[],
  sampleSize = VERIFY_SAMPLE,
): { checked: number; hits: number; rate: number } {
  // Only substantial paragraphs — short ones match (or miss) by luck.
  const pool = paras.filter((p) => normalizeForHash(p).replace(/\s+/g, "").length >= 40);
  const src = pool.length >= 5 ? pool : paras;
  if (!src.length) return { checked: 0, hits: 0, rate: 0 };
  const step = Math.max(1, Math.floor(src.length / sampleSize));
  let checked = 0;
  let hits = 0;
  for (let i = 0; i < src.length && checked < sampleSize; i += step) {
    const stripped = normalizeForHash(src[i]).replace(/\s+/g, "");
    if (stripped.length < 2) continue;
    checked++;
    if (map.byHash.get(textHash(stripped))) { hits++; continue; }
    if (map.entries.some((e) => e.s.includes(stripped) || stripped.includes(e.s))) hits++;
  }
  return { checked, hits, rate: checked ? hits / checked : 0 };
}

/** Try every source for a just-opened book. A shipped artifact may be keyed by
 *  classic id (classics/starter shelf), by content sha256 (cloud-library books
 *  we pre-translated on the host, e.g. QualityLand), by normalized title (when
 *  the user's copy isn't byte-identical so the sha misses), or by filename
 *  stem; then the per-device Dexie build. Returns true if a map ends up loaded.
 *
 *  `opts.sampleParas` is a lazy provider for a handful of the book's real
 *  paragraphs. When supplied, a candidate must actually RESOLVE those before we
 *  bind it — otherwise the title fallback could hand a book the wrong
 *  translation, which is worse than falling back to live translation. It's only
 *  invoked if some candidate artifact actually downloads. 2026-07-19. */
export async function loadPretranslationForBook(
  bookId: string,
  fileName?: string,
  cloudSha256?: string,
  opts?: { title?: string; author?: string; sampleParas?: () => Promise<string[]> },
): Promise<boolean> {
  const candidates: { id: string; via: string }[] = [];
  const classicId = classicIdFromFileName(fileName);
  if (classicId) candidates.push({ id: classicId, via: "filename-alias" });
  if (cloudSha256) candidates.push({ id: cloudSha256, via: "sha256" });
  const byTitle = titleAliasId(opts?.title, opts?.author);
  if (byTitle && !candidates.some((c) => c.id === byTitle))
    candidates.push({ id: byTitle, via: "title-alias" });
  const stem = fileName?.replace(/\.epub$/i, "");
  if (stem && stem !== classicId) candidates.push({ id: stem, via: "filename-stem" });

  // Fetched at most once, and only when a candidate artifact exists.
  let sample: string[] | null = null;
  const getSample = async (): Promise<string[]> => {
    if (sample) return sample;
    sample = opts?.sampleParas ? await opts.sampleParas().catch(() => []) : [];
    return sample;
  };

  for (const c of candidates) {
    const map = await fetchArtifactMap(c.id);
    if (!map) continue;
    if (opts?.sampleParas) {
      const paras = await getSample();
      if (paras.length) {
        const { checked, hits, rate } = sampleMatchRate(map, paras);
        if (rate < VERIFY_MIN_RATE) {
          console.warn(
            `${LOG} rejected candidate ${c.id} (${c.via}): only ${hits}/${checked} sampled paragraphs matched (${(rate * 100).toFixed(0)}%)`,
          );
          continue;
        }
        memMaps.set(bookId, map);
        setBindStatus(bookId, { bound: true, via: c.via, id: c.id,
                                pairs: map.entries.length, sampleRate: rate });
        console.info(
          `${LOG} bound via ${c.via} → ${c.id}: ${map.entries.length} pairs, sample ${hits}/${checked} (${(rate * 100).toFixed(0)}%)`,
        );
        return true;
      }
    }
    // No sample available (paragraph extraction failed / not requested) —
    // bind unverified rather than leaving the reader on the 50s-per-page path.
    memMaps.set(bookId, map);
    setBindStatus(bookId, { bound: true, via: c.via + "-unverified", id: c.id,
                            pairs: map.entries.length });
    console.info(`${LOG} bound via ${c.via} → ${c.id}: ${map.entries.length} pairs (unverified)`);
    return true;
  }

  const imported = await loadImportedPretranslation(bookId);
  setBindStatus(bookId, imported
    ? { bound: true, via: "dexie-import", pairs: memMaps.get(bookId)?.entries.length ?? 0 }
    : { bound: false, tried: candidates.map((c) => `${c.via}:${c.id}`) });
  console.info(
    imported
      ? `${LOG} bound via dexie-import: ${memMaps.get(bookId)?.entries.length ?? 0} pairs`
      : `${LOG} NO binding for ${bookId} — tried [${candidates.map((c) => `${c.via}:${c.id}`).join(", ") || "none"}]; falling back to live translation`,
  );
  return imported;
}

/** Why a book did or didn't bind to a pretranslation artifact — surfaced in the
 *  reader UI. The console log is useless on a phone, and "it silently used live
 *  translation" is exactly the failure we keep chasing, so make it visible. */
export interface BindStatus {
  bound: boolean;
  via?: string;        // filename-alias | sha256 | title-alias | filename-stem | dexie-import
  id?: string;         // artifact id that bound
  pairs?: number;
  sampleRate?: number; // 0..1 verification match rate, when sampled
  tried?: string[];    // candidates attempted when nothing bound
}
const bindStatus = new Map<string, BindStatus>();

export function getBindStatus(bookId: string): BindStatus {
  return bindStatus.get(bookId) ?? { bound: false };
}

export function setBindStatus(bookId: string, s: BindStatus): void {
  bindStatus.set(bookId, s);
}

export function hasPretranslation(bookId: string): boolean {
  return (memMaps.get(bookId)?.byHash.size ?? 0) > 0;
}

/** Number of pairs actually loaded for a book. The UI badge uses THIS rather
 *  than bindStatus: loadClassicPretranslation/loadImportedPretranslation both
 *  early-return when the map is already in memory, bypassing setBindStatus, so
 *  a genuinely-bound book could report "live". The map itself is the truth. */
export function pretranslationSize(bookId: string): number {
  return memMaps.get(bookId)?.entries.length ?? 0;
}

/** English for a page's paragraphs, or null if the map doesn't cover them (then
 *  the caller falls back to live translation).
 *
 *  The reader CLIPS the first/last paragraph of a page to the visible range, so
 *  those come in as fragments of a full source paragraph and never hash-match.
 *  We match those by substring against the full source paragraphs and return
 *  the full paragraph's English — which is why nearly every page was falling
 *  through to live translation (the "yellow circle") before this. 2026-07-17. */
/** The paragraph that most recently defeated the map, per book — so the reader
 *  can show WHY a bound book still fell back, instead of us guessing. */
const lastMiss = new Map<string, string>();
export function getLastMiss(bookId: string): string | undefined {
  return lastMiss.get(bookId);
}

/** How much of a page must map before we serve it from the pretranslation.
 *  Below this we hand the page to the live translator instead. */
const PAGE_MIN_COVERAGE = 0.6;

export function pageEnglish(bookId: string, paras: string[] | undefined): string | null {
  const m = memMaps.get(bookId);
  if (!m || !paras || paras.length === 0) return null;
  const out: string[] = [];
  let matched = 0;
  let considered = 0;
  for (const p of paras) {
    const stripped = normalizeForHash(p).replace(/\s+/g, "");
    if (stripped.length < 2) continue;
    const exact = m.byHash.get(textHash(stripped));
    if (exact) { out.push(exact); matched++; considered++; continue; }
    // Clipped page-edge fragment: find the full source paragraph that contains
    // it (or that it contains). Require some length so a short fragment can't
    // match the wrong paragraph.
    if (stripped.length >= 12) {
      const hit = m.entries.find(
        (e) => e.s.includes(stripped) || stripped.includes(e.s),
      );
      if (hit) { out.push(hit.e); matched++; considered++; continue; }
      // A substantial paragraph the map can't cover. DON'T throw the whole page
      // away for it — that's what kept a fully-bound book (4626 pairs) grinding
      // through 50s live translations. Keep the original text for this one
      // paragraph, remember it for diagnostics, and let coverage decide below.
      lastMiss.set(bookId, p.slice(0, 160));
      out.push(p);
      considered++;
      continue;
    }
    // NOTE: short unmatched fragments are deliberately NOT counted in
    // `considered`. Counting them tanked coverage on perfectly good pages
    // (2 real paragraphs + 3 clipped fragments scored 40%) and forced them
    // back onto the live translator — the regression that made a bound book
    // report "live" again. Coverage measures SUBSTANTIAL text only.
    // SHORT unmatched fragment (< 12 chars): a clipped page edge, a chapter
    // marker, a bare number, stray punctuation. Skip it rather than discarding
    // an otherwise fully-mapped page — failing here is what made a book stick a
    // few pages in and never get past the translate bubble. 2026-07-19.
    continue;
  }
  // De-dup adjacent repeats (a clipped edge and its full paragraph can both
  // resolve to the same English).
  const dedup: string[] = [];
  for (const s of out) if (dedup[dedup.length - 1] !== s) dedup.push(s);
  if (!dedup.length) return null;
  // Only serve the page from the map if it actually covered most of it.
  // A page where we matched almost nothing is better handled live.
  const coverage = considered ? matched / considered : 0;
  if (coverage < PAGE_MIN_COVERAGE) return null;
  return dedup.join("\n\n");
}

/** English for ONE source paragraph, or null if the bound map doesn't cover it.
 *  Used by the in-place translator (FoliateReaderView) to swap a single
 *  paragraph's text so the book paginates in English natively. Exact hash
 *  first, then substring both ways for clipped/edge fragments. */
export function paraEnglish(bookId: string, src: string): string | null {
  const m = memMaps.get(bookId);
  if (!m || !src) return null;
  const stripped = normalizeForHash(src).replace(/\s+/g, "");
  if (stripped.length < 2) return null;
  const exact = m.byHash.get(textHash(stripped));
  if (exact) return exact;
  if (stripped.length >= 12) {
    const hit = m.entries.find((e) => e.s.includes(stripped) || stripped.includes(e.s));
    if (hit) return hit.e;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Build an imported book's pre-translation in the background: translate every
// paragraph once through /lib/translate and persist to Dexie. Chunked + paced
// so it doesn't hammer the single-stream LLM; resumable across sessions via the
// stored pairs. Fire-and-forget from the import flow.
// ─────────────────────────────────────────────────────────────────────────

const TRANSLATE_TIMEOUT_MS = 150_000;

async function translateParagraph(text: string, signal?: AbortSignal): Promise<string | null> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), TRANSLATE_TIMEOUT_MS);
  const onOuter = () => ctrl.abort();
  signal?.addEventListener("abort", onOuter);
  try {
    const r = await fetch(remoteUrl("/lib/translate"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, target_lang: "English" }),
      signal: ctrl.signal,
    });
    if (!r.ok) return null;
    const data = (await r.json()) as { translated?: string };
    return (data.translated || "").trim() || null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", onOuter);
  }
}

let buildingFor: string | null = null;

/** Kick off (or resume) a background full-book translation for an imported
 *  non-English book. Idempotent per session; persists progress to Dexie so a
 *  reload continues where it left off. `onProgress` is optional UI feedback. */
export async function buildImportedPretranslation(
  bookId: string,
  lang: string,
  paragraphs: string[],
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (buildingFor === bookId) return;
  buildingFor = bookId;
  try {
    const existing = await db.pretranslations.get(bookId).catch(() => undefined);
    const pairs: PretranslationPair[] = existing?.pairs ? [...existing.pairs] : [];
    const done = new Set(pairs.map((p) => normalizeForHash(p.s)));
    const uniq: string[] = [];
    const seen = new Set<string>();
    for (const p of paragraphs) {
      const c = normalizeForHash(p);
      if (c.length < 2 || seen.has(c)) continue;
      seen.add(c);
      uniq.push(c);
    }
    let count = pairs.length;
    for (let i = 0; i < uniq.length; i++) {
      if (signal?.aborted) return;
      const src = uniq[i];
      if (done.has(src)) continue;
      const en = await translateParagraph(src, signal);
      if (en) {
        pairs.push({ s: src, e: en });
        done.add(src);
        count++;
        if (count % 4 === 0 || i === uniq.length - 1) {
          await db.pretranslations
            .put({ bookId, lang, pairs, updatedAt: Date.now() })
            .catch(() => { /* quota — keep going in memory */ });
          onProgress?.(count, uniq.length);
        }
      }
    }
    await db.pretranslations
      .put({ bookId, lang, pairs, updatedAt: Date.now() })
      .catch(() => { /* ignore */ });
    // Make it live for the current session immediately.
    memMaps.set(bookId, buildMap(pairs));
    onProgress?.(pairs.length, uniq.length);
  } finally {
    if (buildingFor === bookId) buildingFor = null;
  }
}
